import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Check, ChevronDown, ChevronRight, Copy, ExternalLink, EyeOff, RefreshCw, Video, X} from 'lucide-react';
import styles from './VideoDuplicateReviews.module.css';
import {PRISMAX_DATA_PIPELINE_URL} from '../../../configs/config';
import {SwapMainAppContext} from '../../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../../utils/authErrors';

const UPLOADS_PER_PAGE = 10;
const EPISODES_PER_PAGE = 100;
const HIDDEN_TASK_ID = '12';
const CHAIN_COLORS = {
    Solana: {background: '#1a2a1a', color: '#4caf7d'},
    Ethereum: {background: '#1a1a2e', color: '#7b8fd4'},
    Base: {background: '#0f1e2e', color: '#4e8fd4'},
    Monad: {background: '#1a1030', color: '#a07bd4'},
    Aptos: {background: '#0f2a20', color: '#4ccfa0'},
};

function formatDateTime(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatScore(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '-';
    return `${number.toFixed(1)}%`;
}

function normalizeSlot(slot) {
    const text = String(slot || '').trim();
    return text ? text.toUpperCase() : '-';
}

function decisionDetails(status) {
    const normalized = String(status || '').trim().toUpperCase();
    if (normalized === 'APPROVED') {
        return {label: 'Duplicate', className: styles.statusApproved};
    }
    if (normalized === 'REJECTED') {
        return {label: 'Not duplicate', className: styles.statusRejected};
    }
    return {label: 'Pending', className: ''};
}

function hasHiddenTaskId(review) {
    const sourceTaskId = review?.source?.task_id ?? review?.task_id;
    return String(sourceTaskId || '').trim() === HIDDEN_TASK_ID;
}

function getUploadGroupKey(review) {
    const uploadId = review?.source?.upload_id ?? review?.upload_id;
    if (uploadId !== undefined && uploadId !== null && String(uploadId).trim()) {
        return `upload-${String(uploadId).trim()}`;
    }
    return `review-${review?.review_id || 'unknown'}`;
}

const CopyWalletButton = ({walletAddress}) => {
    const [copied, setCopied] = useState(false);
    const copyWallet = () => {
        const copyPromise = navigator.clipboard?.writeText(walletAddress);
        if (!copyPromise) return;
        copyPromise.then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
        }).catch(() => {});
    };
    return (
        <button type="button" className={styles.copyButton} onClick={copyWallet} title="Copy wallet address">
            {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
        </button>
    );
};

const UserIdentity = ({user, showCopy = true}) => {
    if (user.wallet_address) {
        const wallet = String(user.wallet_address);
        const truncatedWallet = `${wallet.slice(0, 6)}...${wallet.slice(-6)}`;
        const chainStyle = CHAIN_COLORS[user.wallet_chain] || {background: '#2a2a2a', color: '#aaa'};
        return (
            <div className={styles.userCell}>
                <div className={styles.walletRow}>
                    <span className={styles.walletAddress} title={wallet}>{truncatedWallet}</span>
                    {showCopy ? <CopyWalletButton walletAddress={wallet} /> : null}
                </div>
                {user.wallet_chain ? (
                    <span className={styles.chainBadge} style={chainStyle}>{user.wallet_chain}</span>
                ) : null}
                {user.email ? <span className={styles.userSecondary}>{user.email}</span> : null}
                <span className={styles.userId}>ID: {user.user_id}</span>
            </div>
        );
    }
    return (
        <div className={styles.userCell}>
            <span className={styles.userPrimary}>{user.email || 'Unknown user'}</span>
            <span className={styles.userId}>ID: {user.user_id}</span>
        </div>
    );
};

const VideoDuplicateReviews = ({adminToken: adminTokenProp = ''}) => {
    const {showNotification, handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const adminToken = adminTokenProp || localStorage.getItem('prismax_admin_token') || '';
    const [showReviewed, setShowReviewed] = useState(true);
    const [hideTask12, setHideTask12] = useState(false);
    const [hideTask12UserStats, setHideTask12UserStats] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [offset, setOffset] = useState(0);
    const [episodeOffset, setEpisodeOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [cursorHistory, setCursorHistory] = useState([]);
    const [totalUploadCount, setTotalUploadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingId, setIsSavingId] = useState(null);
    const savingReviewIdsRef = useRef(new Set());
    const [expandedGroupKeys, setExpandedGroupKeys] = useState([]);
    const [userStats, setUserStats] = useState([]);
    const [userStatsTotal, setUserStatsTotal] = useState(0);
    const [isLoadingUserStats, setIsLoadingUserStats] = useState(false);
    const [userStatsError, setUserStatsError] = useState('');
    const [error, setError] = useState('');

    const hasAdminAccess = Boolean(adminToken);

    const visibleReviews = useMemo(() => {
        if (!hideTask12) return reviews;
        return reviews.filter((review) => !hasHiddenTaskId(review));
    }, [hideTask12, reviews]);

    const reviewGroups = useMemo(() => {
        const groups = new Map();
        visibleReviews.forEach((review) => {
            const key = getUploadGroupKey(review);
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    uploadId: review?.source?.upload_id ?? review?.upload_id ?? '-',
                    source: review?.source,
                    uploader: review?.source?.uploader,
                    reviews: [],
                });
            }
            groups.get(key).reviews.push(review);
        });
        return Array.from(groups.values());
    }, [visibleReviews]);

    const visibleEpisodeCount = useMemo(() => (
        new Set(visibleReviews.map((review) => review?.source?.episode_id ?? review?.source_episode_id)).size
    ), [visibleReviews]);

    const statusSummary = useMemo(() => {
        if (!totalUploadCount) {
            return visibleReviews.length
                ? `${reviewGroups.length} uploads · ${visibleEpisodeCount} episodes on this page`
                : 'No reviews';
        }
        if (hideTask12) {
            return `${reviewGroups.length} uploads · ${visibleEpisodeCount} episodes shown`;
        }
        return `${reviewGroups.length} uploads · ${visibleEpisodeCount} episodes on this page · ${totalUploadCount} uploads total`;
    }, [hideTask12, reviewGroups.length, totalUploadCount, visibleEpisodeCount, visibleReviews.length]);

    const currentPage = cursorHistory.length + 1;
    const firstUploadNumber = reviewGroups.length ? offset + 1 : 0;
    const lastUploadNumber = reviewGroups.length
        ? Math.min(offset + reviewGroups.length, totalUploadCount || offset + reviewGroups.length)
        : 0;
    const paginationSummary = totalUploadCount
        ? `Uploads ${firstUploadNumber}–${lastUploadNumber} of ${totalUploadCount}`
        : 'No uploads';

    const loadReviews = useCallback(async (nextOffset = 0, nextEpisodeOffset = 0) => {
        if (!adminToken) {
            setReviews([]);
            setOffset(0);
            setEpisodeOffset(0);
            setHasMore(false);
            setNextCursor(null);
            setTotalUploadCount(0);
            return false;
        }

        setIsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                admin: 'true',
                status: showReviewed ? 'ALL' : 'PENDING',
                group_by: 'upload',
                hide_task_12: String(hideTask12),
                limit: String(UPLOADS_PER_PAGE),
                offset: String(nextOffset),
                episode_limit: String(EPISODES_PER_PAGE),
                episode_offset: String(nextEpisodeOffset),
            });
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/video-duplicate-reviews?${params.toString()}`, {
                headers: {Authorization: `Bearer ${adminToken}`},
            });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(getApiErrorMessage(json, 'Failed to load duplicate reviews.'));
            }

            const pagination = json?.pagination || {};
            const nextReviews = Array.isArray(json?.data) ? json.data : [];
            setReviews(nextReviews);
            setExpandedGroupKeys((currentKeys) => {
                const availableKeys = new Set(nextReviews.map(getUploadGroupKey));
                return currentKeys.filter((key) => availableKeys.has(key));
            });
            setOffset(Number(pagination?.offset || 0));
            setEpisodeOffset(Number(pagination?.episode_offset || 0));
            setHasMore(Boolean(pagination?.has_more));
            setNextCursor(pagination?.has_more ? {
                offset: Number(pagination?.next_offset || 0),
                episodeOffset: Number(pagination?.next_episode_offset || 0),
            } : null);
            setTotalUploadCount(Number(pagination?.total_upload_count || 0));
            return true;
        } catch (err) {
            const message = err?.message || 'Failed to load duplicate reviews.';
            setError(message);
            if (isSessionExpiredError(message)) {
                handleAdminSessionExpired?.();
            }
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [adminToken, handleAdminSessionExpired, hideTask12, showReviewed]);

    useEffect(() => {
        setCursorHistory([]);
        loadReviews(0);
    }, [loadReviews]);

    const loadUserStats = useCallback(async () => {
        if (!adminToken) {
            setUserStats([]);
            setUserStatsTotal(0);
            return;
        }
        setIsLoadingUserStats(true);
        setUserStatsError('');
        try {
            const response = await fetch(
                `${PRISMAX_DATA_PIPELINE_URL}/data/admin/video-duplicate-review-user-stats?limit=100&admin=true&hide_task_12=${hideTask12UserStats}`,
                {headers: {Authorization: `Bearer ${adminToken}`}},
            );
            const json = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(getApiErrorMessage(json, 'Failed to load duplicate upload user stats.'));
            }
            setUserStats(Array.isArray(json?.data) ? json.data : []);
            setUserStatsTotal(Number(json?.pagination?.total_count || 0));
        } catch (err) {
            const message = err?.message || 'Failed to load duplicate upload user stats.';
            setUserStatsError(message);
            if (isSessionExpiredError(message)) {
                handleAdminSessionExpired?.();
            }
        } finally {
            setIsLoadingUserStats(false);
        }
    }, [adminToken, handleAdminSessionExpired, hideTask12UserStats]);

    useEffect(() => {
        loadUserStats();
    }, [loadUserStats]);

    const updateDecision = useCallback(async (reviewId, decision) => {
        if (!adminToken || !reviewId || savingReviewIdsRef.current.has(reviewId)) return;
        savingReviewIdsRef.current.add(reviewId);
        const label = decision === 'DUPLICATED' ? 'duplicated' : 'not duplicated';
        const note = window.prompt(`Optional note for ${label}:`, '');
        if (note === null) {
            savingReviewIdsRef.current.delete(reviewId);
            return;
        }

        setIsSavingId(reviewId);
        try {
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/video-duplicate-reviews/${reviewId}/decision?admin=true`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${adminToken}`,
                },
                body: JSON.stringify({decision, note}),
            });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(getApiErrorMessage(json, 'Failed to update duplicate review.'));
            }
            const updatedReview = json?.data || {};
            setReviews((currentReviews) => currentReviews.map((review) => (
                String(review.review_id) === String(reviewId)
                    ? {
                        ...review,
                        status: updatedReview.status || (decision === 'DUPLICATED' ? 'APPROVED' : 'REJECTED'),
                        reviewed_by_admin_id: updatedReview.reviewed_by_admin_id,
                        reviewed_at: updatedReview.reviewed_at,
                        admin_note: updatedReview.admin_note,
                    }
                    : review
            )));
            showNotification?.(`Review ${reviewId} marked ${label}.`, 'success');
            loadReviews(offset, episodeOffset);
            loadUserStats();
        } catch (err) {
            const message = err?.message || 'Failed to update duplicate review.';
            showNotification?.(message, 'error');
            if (isSessionExpiredError(message)) {
                handleAdminSessionExpired?.();
            }
        } finally {
            savingReviewIdsRef.current.delete(reviewId);
            setIsSavingId(null);
        }
    }, [adminToken, episodeOffset, handleAdminSessionExpired, loadReviews, loadUserStats, offset, showNotification]);

    const renderVideoLink = (video) => {
        if (!video?.video_url) return <span className={styles.muted}>No video link</span>;
        return (
            <a className={styles.openLink} href={video.video_url} target="_blank" rel="noreferrer">
                Open video
                <ExternalLink size={13} aria-hidden="true" />
            </a>
        );
    };

    const renderVideoPanel = (video, label) => {
        const path = video?.relative_path || video?.object_path || '-';
        const machine = video?.product_name || (video?.machine_id ? `Machine ${video.machine_id}` : 'Machine -');
        return (
            <section className={styles.videoPanel}>
                <div className={styles.videoPanelHeader}>
                    <span className={styles.videoLabel}>
                        <Video size={14} aria-hidden="true" />
                        {label}
                    </span>
                    {renderVideoLink(video)}
                </div>
                <h3 className={styles.videoTitle}>Episode {video?.episode_id || '-'}</h3>
                <div className={styles.videoFacts}>
                    <span>Upload {video?.upload_id || '-'}</span>
                    <span>Task {video?.task_id || '-'}</span>
                    <span>{machine}</span>
                </div>
                <p className={styles.scenario} title={video?.scenario || '-'}>{video?.scenario || '-'}</p>
                <span className={styles.pathText} title={path}>{path}</span>
            </section>
        );
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroupKeys((currentKeys) => (
            currentKeys.includes(groupKey)
                ? currentKeys.filter((key) => key !== groupKey)
                : [...currentKeys, groupKey]
        ));
    };

    if (!hasAdminAccess) {
        return <div className={styles.errorState}>Admin access required.</div>;
    }

    return (
        <div className={styles.page}>
            <header className={styles.pageHeader}>
                <div>
                    <h2 className={styles.title}>Video Duplicate Reviews</h2>
                    <p className={styles.subtitle}>Compare each uploaded video with its closest match and record a decision.</p>
                </div>
                <div className={styles.toolbar}>
                    <div className={styles.filterGroup} aria-label="Review status filter">
                        <button
                            type="button"
                            className={`${styles.filterButton} ${!showReviewed ? styles.filterButtonActive : ''}`}
                            onClick={() => setShowReviewed(false)}
                            disabled={isLoading}
                            aria-pressed={!showReviewed}
                        >
                            Pending
                        </button>
                        <button
                            type="button"
                            className={`${styles.filterButton} ${showReviewed ? styles.filterButtonActive : ''}`}
                            onClick={() => setShowReviewed(true)}
                            disabled={isLoading}
                            aria-pressed={showReviewed}
                        >
                            All reviews
                        </button>
                    </div>
                    <button
                        type="button"
                        className={`${styles.utilityButton} ${hideTask12 ? styles.utilityButtonActive : ''}`}
                        onClick={() => setHideTask12((current) => !current)}
                        aria-pressed={hideTask12}
                        title="Exclude task ID 12 from review groups"
                    >
                        <EyeOff size={14} aria-hidden="true" />
                        Hide test data
                    </button>
                    <button
                        type="button"
                        className={styles.utilityButton}
                        onClick={() => {
                            loadReviews(offset, episodeOffset);
                            loadUserStats();
                        }}
                        disabled={isLoading}
                    >
                        <RefreshCw className={isLoading ? styles.spin : ''} size={14} aria-hidden="true" />
                        Refresh
                    </button>
                </div>
            </header>

            {error ? <div className={styles.errorState}>{error}</div> : null}

            <div className={styles.summaryRow}>
                <span>{statusSummary}</span>
                {isLoading ? <span>Loading...</span> : null}
            </div>

            {!isLoading && visibleReviews.length === 0 ? (
                <div className={styles.emptyState}>No duplicate reviews found for this filter.</div>
            ) : (
                <div className={styles.groupList}>
                    {reviewGroups.map((group) => {
                        const isExpanded = expandedGroupKeys.includes(group.key);
                        const pendingCount = group.reviews.filter((review) => {
                            const status = String(review.status || '').trim().toUpperCase();
                            return status !== 'APPROVED' && status !== 'REJECTED';
                        }).length;
                        const highestScore = group.reviews.reduce((highest, review) => {
                            const score = Number(review.score);
                            return Number.isFinite(score) ? Math.max(highest, score) : highest;
                        }, Number.NEGATIVE_INFINITY);
                        const machine = group.source?.product_name
                            || (group.source?.machine_id ? `Machine ${group.source.machine_id}` : 'Machine -');
                        return (
                            <section className={styles.groupCard} key={group.key}>
                                <button
                                    type="button"
                                    className={styles.groupHeader}
                                    onClick={() => toggleGroup(group.key)}
                                    aria-expanded={isExpanded}
                                >
                                    <span className={styles.groupIdentity}>
                                        <span className={styles.groupChevron}>
                                            {isExpanded
                                                ? <ChevronDown size={18} aria-hidden="true" />
                                                : <ChevronRight size={18} aria-hidden="true" />}
                                        </span>
                                        <span>
                                            <span className={styles.groupTitleRow}>
                                                <strong className={styles.groupTitle}>Upload {group.uploadId}</strong>
                                                {group.source?.scenario ? (
                                                    <span className={styles.scenarioBadge} title={group.source.scenario}>
                                                        {group.source.scenario}
                                                    </span>
                                                ) : null}
                                            </span>
                                            <span className={styles.groupDetails}>
                                                {group.reviews.length} {group.reviews.length === 1 ? 'match' : 'matches'} · {machine}
                                            </span>
                                        </span>
                                    </span>
                                    {group.uploader ? (
                                        <span className={styles.groupUploader}>
                                            <UserIdentity user={group.uploader} showCopy={false} />
                                        </span>
                                    ) : null}
                                    <span className={styles.groupSignals}>
                                        <span className={styles.groupMetric}>
                                            <small>Highest similarity</small>
                                            <strong>{Number.isFinite(highestScore) ? formatScore(highestScore) : '-'}</strong>
                                        </span>
                                        <span className={`${styles.pendingBadge} ${pendingCount === 0 ? styles.pendingBadgeDone : ''}`}>
                                            {pendingCount === 0 ? 'Reviewed' : `${pendingCount} pending`}
                                        </span>
                                    </span>
                                </button>

                                {isExpanded ? (
                                    <div className={styles.groupBody}>
                                        <div className={styles.reviewList}>
                                            {group.reviews.map((review) => {
                                                const saving = isSavingId === review.review_id;
                                                const decision = decisionDetails(review.status);
                                                return (
                                                    <article className={styles.reviewCard} key={review.review_id}>
                                                        <div className={styles.cardHeader}>
                                                            <div className={styles.cardMeta}>
                                                                <span className={styles.date}>{formatDateTime(review.created_at)}</span>
                                                                <span className={styles.slot}>
                                                                    {normalizeSlot(review.video_slot || review.source?.video_slot || review.matched?.video_slot)}
                                                                </span>
                                                            </div>
                                                            <div className={styles.reviewId}>
                                                                <span>Review ID</span>
                                                                <strong>{review.review_id ?? '-'}</strong>
                                                            </div>
                                                            <div className={styles.cardSignals}>
                                                                <div className={styles.scoreBlock}>
                                                                    <span className={styles.scoreLabel}>Similarity</span>
                                                                    <span className={styles.scoreBadge}>{formatScore(review.score)}</span>
                                                                </div>
                                                                <span className={`${styles.statusBadge} ${decision.className}`}>{decision.label}</span>
                                                            </div>
                                                        </div>

                                                        <div className={styles.comparisonGrid}>
                                                            {renderVideoPanel(review.source, 'Uploaded video')}
                                                            {renderVideoPanel(review.matched, 'Similar video')}
                                                        </div>

                                                        <div className={styles.cardFooter}>
                                                            <span className={styles.decisionHint}>Do these videos show the same recording?</span>
                                                            <div className={styles.cardActions}>
                                                                <button
                                                                    type="button"
                                                                    className={styles.decisionButton}
                                                                    onClick={() => updateDecision(review.review_id, 'NOT_DUPLICATED')}
                                                                    disabled={saving || review.status === 'REJECTED'}
                                                                >
                                                                    <X size={14} aria-hidden="true" />
                                                                    {saving ? 'Saving...' : 'Not a duplicate'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.decisionButton} ${styles.duplicateButton}`}
                                                                    onClick={() => updateDecision(review.review_id, 'DUPLICATED')}
                                                                    disabled={saving || review.status === 'APPROVED'}
                                                                >
                                                                    <Check size={14} aria-hidden="true" />
                                                                    {saving ? 'Saving...' : 'Mark duplicate'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : null}
                            </section>
                        );
                    })}
                </div>
            )}

            <nav className={styles.pagination} aria-label="Duplicate reviews pagination">
                <button
                    type="button"
                    className={styles.pageButton}
                    onClick={async () => {
                        const previousCursor = cursorHistory[cursorHistory.length - 1];
                        if (!previousCursor) return;
                        const loaded = await loadReviews(previousCursor.offset, previousCursor.episodeOffset);
                        if (loaded) setCursorHistory((history) => history.slice(0, -1));
                    }}
                    disabled={cursorHistory.length === 0 || isLoading}
                >
                    Previous
                </button>
                <div className={styles.pageInfo} aria-live="polite">
                    <strong>Page {currentPage}</strong>
                    <span>{paginationSummary} · {visibleEpisodeCount} episodes</span>
                </div>
                <button
                    type="button"
                    className={styles.pageButton}
                    onClick={async () => {
                        if (!nextCursor) return;
                        const currentCursor = {offset, episodeOffset};
                        const loaded = await loadReviews(nextCursor.offset, nextCursor.episodeOffset);
                        if (loaded) setCursorHistory((history) => [...history, currentCursor]);
                    }}
                    disabled={!hasMore || isLoading}
                >
                    Next
                </button>
            </nav>

            <section className={styles.rankingSection}>
                <div className={styles.rankingHeader}>
                    <div>
                        <div className={styles.rankingTitleRow}>
                            <h3 className={styles.rankingTitle}>Duplicate uploads by user</h3>
                            <span className={styles.rankingCount}>{userStatsTotal}</span>
                        </div>
                        <p className={styles.rankingSubtitle}>Ranked by the number of duplicate matches detected after upload.</p>
                    </div>
                    <button
                        type="button"
                        className={`${styles.utilityButton} ${hideTask12UserStats ? styles.utilityButtonActive : ''}`}
                        onClick={() => setHideTask12UserStats((current) => !current)}
                        disabled={isLoadingUserStats}
                        aria-pressed={hideTask12UserStats}
                        title="Exclude task ID 12 from this ranking"
                    >
                        <EyeOff size={14} aria-hidden="true" />
                        Hide test data
                    </button>
                </div>

                {userStatsError ? <div className={styles.statsError}>{userStatsError}</div> : null}
                {isLoadingUserStats ? <div className={styles.statsState}>Loading user stats...</div> : null}
                {!isLoadingUserStats && !userStatsError && userStats.length === 0 ? (
                    <div className={styles.statsState}>No duplicate upload activity found.</div>
                ) : null}
                {!isLoadingUserStats && !userStatsError && userStats.length > 0 ? (
                    <div className={styles.statsTableWrapper}>
                        <table className={styles.statsTable}>
                            <thead>
                            <tr>
                                <th className={styles.rankColumn}>#</th>
                                <th>User</th>
                                <th>Detected matches</th>
                                <th>Affected uploads</th>
                                <th>Confirmed</th>
                                <th>Pending</th>
                            </tr>
                            </thead>
                            <tbody>
                            {userStats.map((user, index) => (
                                <tr key={user.user_id}>
                                    <td className={styles.rankCell}>{index + 1}</td>
                                    <td><UserIdentity user={user} /></td>
                                    <td className={styles.numericCell}>{Number(user.duplicate_detection_count || 0).toLocaleString('en-US')}</td>
                                    <td className={styles.numericCell}>{Number(user.affected_upload_count || 0).toLocaleString('en-US')}</td>
                                    <td className={styles.numericCell}>{Number(user.confirmed_duplicate_count || 0).toLocaleString('en-US')}</td>
                                    <td className={styles.numericCell}>{Number(user.pending_review_count || 0).toLocaleString('en-US')}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </section>
        </div>
    );
};

export default VideoDuplicateReviews;
